<template>
  <div>
    <v-container
      fluid
      class="pa-7">
      <HeaderComponent
        class="pb-4"
        :title="$vuetify.lang.t('$vuetify.ecoChallengeList.title')"
      >
        <template #actions>
          <v-spacer class="flex-grow-3" />
          <v-text-field
            :label="$vuetify.lang.t('$vuetify.ecoChallengeList.input')"
            outlined
            single-line
            hide-details
            class="mr-4"
            :value="searchQuery"
            @input="checkSearchQuery($event)"
            @click:clear="clear"
            clearable
            :disabled="filterActive"
          />
          <v-btn @click="showFilters()" :color="filterActive ? '' : 'primary'" class="h-full">
            <v-icon v-if="filterActive">$vuetify.icons.clear</v-icon>
            <v-icon v-else>$vuetify.icons.filter</v-icon>
          </v-btn>
        </template>
      </HeaderComponent>

      <filter-component v-if="filterActive" @getItems="getFilteredItems($event)"></filter-component>

      <v-card class="mt-3">
        <v-data-table
          :headers="headers"
          :items="orders"
          itemKey="id"
          :loading="ordersFetchStatus === REQUEST_STATUS.LOADING"
          :serverItemsLength="total"
          :options.sync="options"
          :footerProps="{'items-per-page-options': [10, 20, 50, 100, 500], options, pagination }"
          @click:row="redirectToDetail"
        >
          <template v-slot:item.id="{ item }">
            {{ item.externalId }}
          </template>
          <template v-slot:item.consignment_code="{ item }">
            {{ item.payload.consignment_code }}
          </template>
          <template v-slot:item.created_at="{ item }">
            {{ formatDateTime(item.createdAt) }}
          </template>
          <template v-slot:item.customer_id="{ item }">
            {{ item.customerId }}
            -
            {{ item.firstName }}
            {{ item.lastName }}
            -
            {{ item.email }}
          </template>
          <template v-slot:item.type="{ item }">
            {{ $vuetify.lang.t('$vuetify.ecoChallengeType.' + item.type) }}
          </template>
          <template v-slot:item.status="{ item }">
            {{ item.statusProperties.label }}
          </template>
        </v-data-table>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import TableStateManager from '../../common/helpers/tableStateManager';

import HeaderComponent from '../../components/layout/HeaderComponent.vue';
import FilterComponent from '../../components/orders/FilterComponent.vue';

import REQUEST_STATUS from '../../common/enums/requestStatus';
import {formatDateTime} from "@/common/helpers/date";
import {EcoChallengeModel} from "@/common/models/eco-challenge.model";

export default {
  name: 'EcoOrdersList',

  components: {
    HeaderComponent,
    FilterComponent,
  },

  data() {
    return {
      REQUEST_STATUS,
      searchTimer: null,
      searchQuery: null,
      searchPending: false,
      filterActive: false,
      total: 50975,
      options: {sortBy: [EcoChallengeModel.ATTR_CREATED_AT],},
      headers: [
        {
          text: this.$vuetify.lang.t('$vuetify.ecoChallengeList.id'),
          align: 'left',
          value: 'id',
        },
        {
          text: this.$vuetify.lang.t('$vuetify.ecoChallengeList.consignment_code'),
          align: 'left',
          value: 'consignment_code',
          sortable: false,
        },
        {
          text: this.$vuetify.lang.t('$vuetify.ecoChallengeList.created'),
          align: 'left',
          value: 'created_at',
        },
        {
          text: this.$vuetify.lang.t('$vuetify.ecoChallengeList.customer'),
          align: 'left',
          value: 'customer_id',
        },
        {
          text: this.$vuetify.lang.t('$vuetify.ecoChallengeList.transport'),
          align: 'left',
          value: 'type',
        },
        {
          text: this.$vuetify.lang.t('$vuetify.ecoChallengeList.status'),
          align: 'left',
          value: 'status',
        },
      ]
    }
  },

  computed: {
    ...mapGetters({
      orders: 'orders/getAllItems',
      ordersFetchStatus: 'orders/getStatus',
    }),

    pagination() {
      return {
        page: this.options.page,
        itemsPerPage: this.options.itemsPerPage,
        pageStart: this.options.page * this.options.itemsPerPage - this.options.itemsPerPage,
        pageStop: this.options.page * this.options.itemsPerPage,
        pageCount: Math.ceil(this.total / this.options.itemsPerPage),
        itemsLength: 50975,
      };
    },
  },

  watch: {
    options: {
      deep: true,
      handler() {
        const options = TableStateManager.getTableState('eco')

        TableStateManager.setTableState('eco', {
          ...options,
          options: this.options,
        })

        if (this.options.itemsPerPage && !this.searchPending) {
          this.fetchOrdersItemsList({
            options: this.options,
            filterData: options.filterQuery,
          })
        }
      },
    },

    searchQuery() {
      const options = TableStateManager.getTableState('eco')

      TableStateManager.setTableState(this.resource, {
        ...options,
        options: this.options,
        searchQuery: this.searchQuery,
      })

      this.search()
    },
  },

  methods: {
    ...mapActions({
      fetchOrdersItemsList: 'orders/fetchItemsList'
    }),

    formatDateTime,

    async checkSearchQuery(search) {
      const searchQuery = TableStateManager.getTableState('eco', 'searchQuery')

      if (searchQuery !== search) {
        this.searchQuery = search
        this.$set(this.options, 'page', 1)
      }
    },

    search() {
      clearTimeout(this.searchTimer);

      this.searchTimer = setTimeout(() => {
        if (!this.searchPending) {
          this.fetchOrdersItemsList({ options: this.options, searchQuery: this.searchQuery });
        }
      }, 500);
    },

    async getFilteredItems(filters) {
      this.searchPending = true

      this.$set(this.options, 'page', 1)
      await this.fetchOrdersItemsList({options: this.options, filterData: filters})

      this.searchPending = false
    },

    clear() {
      this.searchQuery = null
    },

    redirectToDetail(event) {
      this.$router.push({ name: 'orders.detail', params: { id: event.id } });
    },

    showFilters() {
      this.filterActive = !this.filterActive

      this.clear()
    },
  },

  created() {
    const { options, filterQuery, searchQuery } = TableStateManager.getTableState('eco')
    this.options = {
      sortBy: [EcoChallengeModel.ATTR_CREATED_AT],
      ...options,
    }

    const moreFieldThanPriceAndDate = 2;

    if (
      filterQuery &&
      (Object.keys(filterQuery).length > moreFieldThanPriceAndDate ||
        Object.keys(filterQuery.price).length ||
        Object.keys(filterQuery.createdAt).length)
    ) {
      this.filterActive = true
    } else {
      this.searchQuery = searchQuery
    }
  },
}
</script>

<style lang="scss">
.h-full {
  height: 56px !important;
}
</style>
